import {
  BUSINESS_KINDS,
  BUSINESS_KIND_LABELS,
  type BusinessKind,
  LEAD_SEGMENTS,
  OWNERSHIP_TYPES,
  OWNERSHIP_TYPE_LABELS,
  PIZZA_FOCUS,
  PIZZA_FOCUS_LABELS,
  RESTAURANT_TYPE_SUGGESTIONS,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Building2, Save, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, Field, Input, Select, Textarea, useToast } from '../../components/ui';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useLookups } from '../../hooks/useMeta';
import { useNavSection } from '../../hooks/useNavSection';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { kindNoun, listOf } from './model';

type Match = {
  businessId: string;
  name: string;
  city: string | null;
  addressLine1: string | null;
  confidence: number;
  signals: { signal: string; points: number; detail: string }[];
};

type FormState = {
  name: string;
  category: string;
  restaurantType: string;
  pizzaFocus: string;
  ownershipType: string;
  locationCount: string;
  operatingStatus: string;
  addressLine1: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone: string;
  website: string;
  email: string;
  contactFirstName: string;
  contactLastName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  contactRole: string;
  consentBasis: string;
  ownerId: string;
  sourceId: string;
  notes: string;
};

const EMPTY: FormState = {
  name: '',
  category: 'Pizzeria',
  restaurantType: '',
  pizzaFocus: 'primary',
  ownershipType: 'independent',
  locationCount: '1',
  operatingStatus: 'operating',
  addressLine1: '',
  city: '',
  region: '',
  postalCode: '',
  country: 'CA',
  phone: '',
  website: '',
  email: '',
  contactFirstName: '',
  contactLastName: '',
  contactTitle: '',
  contactEmail: '',
  contactPhone: '',
  contactRole: 'owner',
  consentBasis: 'none',
  ownerId: '',
  sourceId: '',
  notes: '',
};

const NEW_COPY: Record<BusinessKind, { description: string; namePlaceholder: string }> = {
  pizzeria: { description: 'Add a pizzeria or restaurant you already know about.', namePlaceholder: 'Pizzeria Fuoco Vivo' },
  dealer: { description: 'Add an equipment dealer that resells our ovens.', namePlaceholder: 'Northern Kitchen Supply' },
  rep: { description: 'Add an independent rep agency that represents our lines.', namePlaceholder: 'Summit Foodservice Agency' },
};

/**
 * Manual lead entry (pizzerias, dealers and reps). The duplicate check runs while the salesperson types, so they see
 * "you already have this pizzeria" before creating a second record.
 */
export function NewLeadPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { meta } = useLookups();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Preset by the list the user came from (/leads/new?kind=dealer); the Type select can still change it.
  const [kind, setKind] = useState<BusinessKind>(() => BUSINESS_KINDS.find((value) => value === params.get('kind')) ?? 'pizzeria');
  const isPizzeria = kind === 'pizzeria';
  // New pizzerias from the Prospects / Clients / Test Kitchens lists start there (/leads/new?kind=pizzeria&segment=client).
  const segment = LEAD_SEGMENTS.find((value) => value === params.get('segment')) ?? 'lead';
  const noun = kindNoun(kind);
  const { path: listPath, label: listLabel } = listOf({ kind, segment });
  useNavSection(listPath);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((current) => ({ ...current, [key]: value }));

  const nameForCheck = useDebouncedValue(form.name, 400);
  const phoneForCheck = useDebouncedValue(form.phone, 400);
  const checkQuery = { name: nameForCheck, phone: phoneForCheck || undefined, city: form.city || undefined, excludeId: undefined };
  const { data: matches } = useQuery({
    queryKey: ['leads', 'duplicate-check', checkQuery],
    queryFn: ({ signal }) =>
      api.get<{ matches: Match[] }>(
        `/api/leads/duplicate-check?${new URLSearchParams(Object.entries(checkQuery).filter(([, v]) => v) as [string, string][]).toString()}`,
        { signal },
      ),
    enabled: nameForCheck.trim().length >= 3,
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; warnings: string[]; duplicatesFlagged: number }>('/api/leads', {
        name: form.name.trim(),
        kind,
        ...(isPizzeria && segment !== 'lead' ? { segment } : {}),
        // Category, restaurant type, pizza focus and ownership describe pizzeria fit: not asked or sent for dealers/reps.
        ...(isPizzeria
          ? {
              category: form.category || null,
              restaurantType: form.restaurantType || null,
              pizzaFocus: form.pizzaFocus,
              ownershipType: form.ownershipType,
            }
          : {}),
        locationCount: form.locationCount ? Number(form.locationCount) : null,
        operatingStatus: form.operatingStatus,
        addressLine1: form.addressLine1 || null,
        city: form.city || null,
        region: form.region || null,
        postalCode: form.postalCode || null,
        country: form.country || 'CA',
        phone: form.phone || null,
        website: form.website || null,
        email: form.email || null,
        ownerId: form.ownerId || undefined,
        sourceId: form.sourceId || undefined,
        contacts:
          form.contactFirstName || form.contactLastName || form.contactEmail || form.contactPhone
            ? [
                {
                  firstName: form.contactFirstName || null,
                  lastName: form.contactLastName || null,
                  title: form.contactTitle || null,
                  email: form.contactEmail || null,
                  phone: form.contactPhone || null,
                  role: form.contactRole,
                  consentBasis: form.consentBasis as 'none' | 'implied_published' | 'implied_business_relationship' | 'implied_inquiry' | 'express',
                  consentObtainedAt: form.consentBasis === 'none' ? null : new Date().toISOString(),
                  consentSource: form.consentBasis === 'none' ? null : 'Recorded during manual entry',
                },
              ]
            : [],
      }),
    onSuccess: async (created) => {
      toast.success(
        `${isPizzeria ? 'Lead' : BUSINESS_KIND_LABELS[kind]} created${created.duplicatesFlagged > 0 ? ` — ${created.duplicatesFlagged} possible duplicate flagged` : ''}`,
      );
      if (form.notes.trim()) {
        await api.post(`/api/leads/${created.id}/notes`, { body: form.notes.trim() }).catch(() => undefined);
      }
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/leads/${created.id}`);
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      toast.error(errorMessage(error));
    },
  });

  const duplicateWarning = (matches?.matches ?? []).filter((match) => match.confidence >= 55);

  return (
    <Page width="narrow">
      <PageHeader
        title={`New ${noun}`}
        description={NEW_COPY[kind].description}
        breadcrumbs={[{ label: listLabel, to: listPath }, { label: `New ${noun}` }]}
        actions={
          <>
            <Button icon={ArrowLeft} onClick={() => navigate(listPath)}>
              Cancel
            </Button>
            <Button variant="primary" icon={Save} loading={create.isPending} onClick={() => create.mutate()} disabled={form.name.trim() === ''}>
              Save {noun}
            </Button>
          </>
        }
      />

      {duplicateWarning.length > 0 && (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <CardBody>
            <div className="flex items-start gap-2">
              <Search className="mt-0.5 size-4 text-amber-700" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-amber-900">
                  {duplicateWarning.length === 1 ? 'This looks like a lead you already have' : 'These look like leads you already have'}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {duplicateWarning.map((match) => (
                    <li key={match.businessId} className="flex flex-wrap items-center gap-2 text-[13px]">
                      <Badge color="amber" size="sm">
                        {match.confidence}% match
                      </Badge>
                      <button type="button" className="font-medium text-amber-900 underline" onClick={() => navigate(`/leads/${match.businessId}`)}>
                        {match.name}
                      </button>
                      <span className="text-amber-800">{match.city ?? match.addressLine1 ?? ''}</span>
                      <span className="text-xs text-amber-700">{match.signals.map((s) => s.detail).join(' · ')}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-800">Open the existing lead instead, or keep filling this in to add a separate location.</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="mb-4">
        <CardHeader title="Where is it?" description="Name, address and how to reach them." icon={<Building2 className="size-4" />} />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Type" className="sm:col-span-2">
            <Select
              value={kind}
              onChange={(event) => setKind(BUSINESS_KINDS.find((value) => value === event.target.value) ?? 'pizzeria')}
              options={BUSINESS_KINDS.map((value) => ({ value, label: BUSINESS_KIND_LABELS[value] }))}
            />
          </Field>
          <Field label="Business name" required error={errors.name} className="sm:col-span-2">
            <Input value={form.name} onChange={(event) => set('name', event.target.value)} placeholder={NEW_COPY[kind].namePlaceholder} autoFocus />
          </Field>
          <Field label="Street address">
            <Input value={form.addressLine1} onChange={(event) => set('addressLine1', event.target.value)} placeholder="4120 rue Saint-Denis" />
          </Field>
          <Field label="City">
            <Input value={form.city} onChange={(event) => set('city', event.target.value)} placeholder="Montréal" />
          </Field>
          <Field label="Province / state">
            <Input value={form.region} onChange={(event) => set('region', event.target.value)} placeholder="QC" />
          </Field>
          <Field label="Postal code">
            <Input value={form.postalCode} onChange={(event) => set('postalCode', event.target.value)} placeholder="H2W 2M5" />
          </Field>
          <Field label="Country">
            <Select value={form.country} onChange={(event) => set('country', event.target.value)}>
              <option value="CA">Canada</option>
              <option value="US">United States</option>
            </Select>
          </Field>
          <Field label="Phone" error={errors.phone}>
            <Input value={form.phone} onChange={(event) => set('phone', event.target.value)} placeholder="(514) 555-0111" />
          </Field>
          <Field label="Public email" error={errors.email}>
            <Input value={form.email} onChange={(event) => set('email', event.target.value)} placeholder="info@pizzeria.example" />
          </Field>
          <Field label="Website">
            <Input value={form.website} onChange={(event) => set('website', event.target.value)} placeholder="pizzeria.example" />
          </Field>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader
          title={isPizzeria ? 'What kind of place is it?' : 'About the business'}
          description={isPizzeria ? 'Pizza focus and ownership drive the lead score.' : 'Who looks after it and where it came from.'}
        />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          {isPizzeria && (
            <>
              <Field label="Restaurant type" hint="e.g. Neapolitan, Wood-fired, Detroit style">
                <Input
                  value={form.restaurantType}
                  onChange={(event) => set('restaurantType', event.target.value)}
                  list="restaurant-types"
                  placeholder="Neapolitan"
                />
                <datalist id="restaurant-types">
                  {RESTAURANT_TYPE_SUGGESTIONS.map((type) => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </Field>
              <Field label="Pizza focus">
                <Select value={form.pizzaFocus} onChange={(event) => set('pizzaFocus', event.target.value)}>
                  {PIZZA_FOCUS.map((value) => (
                    <option key={value} value={value}>
                      {PIZZA_FOCUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Independent or chain">
                <Select value={form.ownershipType} onChange={(event) => set('ownershipType', event.target.value)}>
                  {OWNERSHIP_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {OWNERSHIP_TYPE_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}
          <Field label="Locations" hint="How many locations the operator runs">
            <Input type="number" min={1} value={form.locationCount} onChange={(event) => set('locationCount', event.target.value)} />
          </Field>
          <Field label="Operating status">
            <Select value={form.operatingStatus} onChange={(event) => set('operatingStatus', event.target.value)}>
              <option value="operating">Operating</option>
              <option value="coming_soon">Coming soon</option>
              <option value="temporarily_closed">Temporarily closed</option>
              <option value="permanently_closed">Permanently closed</option>
              <option value="unknown">Unknown</option>
            </Select>
          </Field>
          <Field label="Assigned to" hint="Defaults to you">
            <Select value={form.ownerId} onChange={(event) => set('ownerId', event.target.value)}>
              <option value="">Me</option>
              {(meta?.users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Source">
            <Select value={form.sourceId} onChange={(event) => set('sourceId', event.target.value)}>
              <option value="">Manual entry</option>
              {(meta?.sources ?? []).map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </Select>
          </Field>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader title="Who do you talk to?" description="Optional — you can add more people later." />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          <Field label="First name">
            <Input value={form.contactFirstName} onChange={(event) => set('contactFirstName', event.target.value)} placeholder="Marco" />
          </Field>
          <Field label="Last name">
            <Input value={form.contactLastName} onChange={(event) => set('contactLastName', event.target.value)} placeholder="Bellini" />
          </Field>
          <Field label="Role">
            <Select value={form.contactRole} onChange={(event) => set('contactRole', event.target.value)}>
              <option value="owner">Owner</option>
              <option value="founder">Founder</option>
              <option value="general_manager">General manager</option>
              <option value="kitchen_manager">Kitchen manager</option>
              <option value="operations_manager">Operations manager</option>
              <option value="purchasing_manager">Purchasing manager</option>
              <option value="chef">Chef</option>
              <option value="other_decision_maker">Other decision maker</option>
              <option value="other">Other</option>
            </Select>
          </Field>
          <Field label="Title">
            <Input value={form.contactTitle} onChange={(event) => set('contactTitle', event.target.value)} placeholder="Owner" />
          </Field>
          <Field label="Email" error={errors['contacts.0.email']}>
            <Input value={form.contactEmail} onChange={(event) => set('contactEmail', event.target.value)} placeholder="marco@pizzeria.example" />
          </Field>
          <Field label="Phone">
            <Input value={form.contactPhone} onChange={(event) => set('contactPhone', event.target.value)} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Notes" description="Anything you already know — it lands on the timeline." />
        <CardBody>
          <Textarea rows={3} value={form.notes} onChange={(event) => set('notes', event.target.value)} placeholder="Owner mentioned the oven overheats on Friday nights." />
          <Field
            label="Consent to email this contact"
            hint="Required before bulk or automated email can reach them (Settings → Compliance)."
            className="mt-3 max-w-md"
          >
            <Select value={form.consentBasis} onChange={(event) => set('consentBasis', event.target.value)}>
              <option value="none">No consent recorded</option>
              <option value="implied_published">Implied — address published on their website</option>
              <option value="implied_business_relationship">Implied — existing business relationship</option>
              <option value="implied_inquiry">Implied — they asked us something</option>
              <option value="express">Express consent</option>
            </Select>
          </Field>
        </CardBody>
      </Card>
    </Page>
  );
}
